/*
  bback custom components
  - <card-variant-picker>: variant picker on product cards
  - <campaign-countdown>: campaign manager countdown
*/

const setPressed = (button, on) => {
  button.classList.toggle('is-selected', on);
  button.setAttribute('aria-pressed', on ? 'true' : 'false');
};

if (!customElements.get('card-variant-picker')) {
  class CardVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.variants = JSON.parse(this.querySelector('script[data-variants]').textContent);
      } catch (error) {
        console.error('card-variant-picker: could not read variants', error);
        return;
      }

      this.card = this.closest('.product-card');
      this.rows = [...this.querySelectorAll('[data-option-index]')];
      this.addEventListener('click', this.onClick.bind(this));
      this.updateAvailability();
    }

    get selectedOptions() {
      return this.rows.map((row) => row.querySelector('.is-selected')?.dataset.value);
    }

    onClick(event) {
      const button = event.target.closest('[data-value]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();

      const row = button.closest('[data-option-index]');
      const index = Number(row.dataset.optionIndex);
      row.querySelectorAll('[data-value]').forEach((pill) => setPressed(pill, pill === button));

      const selected = this.selectedOptions;
      let variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));

      // No exact match: keep the value just picked and choose the closest variant.
      if (!variant) {
        const candidates = this.variants.filter((v) => v.options[index] === button.dataset.value);
        variant = candidates.find((v) => v.available) || candidates[0];
        if (!variant) return;
        this.rows.forEach((r, i) =>
          r.querySelectorAll('[data-value]').forEach((pill) => setPressed(pill, pill.dataset.value === variant.options[i]))
        );
      }

      this.applyVariant(variant);
      this.updateAvailability();
    }

    applyVariant(variant) {
      const card = this.card;
      if (!card) return;
      this.currentVariant = variant;

      // Price + slashed price
      const template = this.querySelector(`template[data-price-for="${variant.id}"]`);
      const priceBox = card.querySelector('[data-card-price]');
      if (template && priceBox) priceBox.replaceChildren(template.content.cloneNode(true));

      // Image
      const image = card.querySelector('.product-card__image');
      if (image && variant.src) {
        image.srcset = variant.srcset;
        image.src = variant.src;
      }

      // Links
      card.querySelectorAll('a[data-card-link]').forEach((link) => (link.href = variant.url));

      // Sale badge on the image
      const badges = card.querySelector('.product-badges-container');
      if (badges && card.hasAttribute('data-card-discount-badge')) {
        let badge = badges.querySelector('.sale-badge');
        if (variant.badge) {
          if (!badge) {
            badge = document.createElement('div');
            badge.className = 'info-badge sale-badge';
            const soldOut = badges.querySelector('.sold-out-badge');
            soldOut ? soldOut.after(badge) : badges.prepend(badge);
          }
          badge.textContent = variant.badge;
          badge.classList.toggle('sale-badge--campaign', variant.campaign === 'campaign');
        } else if (badge) {
          badge.remove();
        }
      }

      // Quick add adds the picked variant
      const quickAdd = card.querySelector('quick-add-button');
      if (quickAdd) {
        quickAdd.setAttribute('variant-id', variant.id);
        const button = quickAdd.querySelector('button');
        if (button) button.disabled = !variant.available;
      }

      this.dispatchEvent(new CustomEvent('card-variant:change', { bubbles: true, detail: { variant } }));
    }

    updateAvailability() {
      const selected = this.selectedOptions;
      this.rows.forEach((row, index) => {
        row.querySelectorAll('[data-value]').forEach((pill) => {
          const match = this.variants.find(
            (v) =>
              v.available &&
              v.options[index] === pill.dataset.value &&
              v.options.every((value, i) => i === index || value === selected[i])
          );
          pill.classList.toggle('is-unavailable', !match);
        });
      });
    }
  }

  customElements.define('card-variant-picker', CardVariantPicker);
}

if (!customElements.get('campaign-countdown')) {
  class CampaignCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end) * 1000;
      if (!this.end) return;
      this.days = this.querySelector('[data-days]');
      this.hours = this.querySelector('[data-hours]');
      this.minutes = this.querySelector('[data-minutes]');
      this.seconds = this.querySelector('[data-seconds]');
      this.daysUnit = this.querySelector('[data-unit-days]');
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const remaining = Math.max(0, this.end - Date.now());
      if (remaining === 0) {
        clearInterval(this.timer);
        (this.closest('[data-campaign-hide]') || this).style.display = 'none';
        return;
      }
      const total = Math.floor(remaining / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      const days = Math.floor(total / 86400);
      if (this.daysUnit) this.daysUnit.hidden = days === 0;
      this.days.textContent = days;
      this.hours.textContent = pad(Math.floor((total % 86400) / 3600));
      this.minutes.textContent = pad(Math.floor((total % 3600) / 60));
      this.seconds.textContent = pad(total % 60);
    }
  }

  customElements.define('campaign-countdown', CampaignCountdown);
}
